import { type IViewCandyMachine } from '~/types/CandyMachine';

export enum ComicBookContentRating {
    EVERYONE = 'EVERYONE',
    TEEN = 'TEEN',
    TEEN_PLUS = 'TEEN_PLUS',
    MATURE = 'MATURE',
}

export enum ComicBookColoring {
    GRAYSCALE = 'GRAYSCALE',
    FULL_COLORS = 'FULL_COLORS',
}

export enum Payments {
    SOL = 'Sol payment',
    TOKEN = 'Token payment',
}

export enum ComicBookPrintState {
    CREATED = 'CREATED',
    METADATA_GENERATING_REQUESTED = 'METADATA_GENERATING_REQUESTED',
    METADATA_GENERATING_FAILED = 'METADATA_GENERATING_FAILED',
    METADATA_GENERATED = 'METADATA_GENERATED',
    ASSETS_GENERATING_REQUESTED = 'ASSETS_GENERATING_REQUESTED',
    ASSETS_GENERATING_FAILED = 'ASSETS_GENERATING_FAILED',
    ASSETS_GENERATED = 'ASSETS_GENERATED',
    ASSETS_APPROVED = 'ASSETS_APPROVED',
    ASSETS_UPLOADED = 'ASSETS_UPLOADED',
    ASSETS_UPLOADING_REQUESTED = 'ASSETS_UPLOADING_REQUESTED',
    ASSETS_UPLOADING_FAILED = 'ASSETS_UPLOADING_FAILED',
    COLLECTION_CREATION_REQUESTED = 'COLLECTION_CREATION_REQUESTED',
    COLLECTION_CREATION_FAILED = 'COLLECTION_CREATION_FAILED',
    COLLECTION_ASSIGNED = 'COLLECTION_ASSIGNED',
    CM_CREATION_REQUESTED = 'CM_CREATION_REQUESTED',
    CM_CREATION_FAILED = 'CM_CREATION_FAILED',
    CM_CREATED = 'CM_CREATED',
    CM_ITEMS_INSERT_REQUESTED = 'CM_ITEMS_INSERT_REQUESTED',
    CM_ITEMS_INSERT_FAILED = 'CM_ITEMS_INSERT_FAILED',
    CM_ITEMS_INSERTED = 'CM_ITEMS_INSERTED',
}

export const ComicBookPrintStateNames: { [key in ComicBookPrintState]: string } = {
    [ComicBookPrintState.CREATED]: 'Print Created',
    [ComicBookPrintState.METADATA_GENERATING_REQUESTED]: 'Metadata Generating Requested',
    [ComicBookPrintState.METADATA_GENERATING_FAILED]: 'Metadata Generating Failed',
    [ComicBookPrintState.METADATA_GENERATED]: 'Metadata Generated',
    [ComicBookPrintState.ASSETS_GENERATING_REQUESTED]: 'Assets Generating Requested',
    [ComicBookPrintState.ASSETS_GENERATING_FAILED]: 'Assets Generating Failed',
    [ComicBookPrintState.ASSETS_GENERATED]: 'Assets Generated',
    [ComicBookPrintState.ASSETS_APPROVED]: 'Upload assets',
    [ComicBookPrintState.ASSETS_UPLOADED]: 'Assets Uploaded',
    [ComicBookPrintState.ASSETS_UPLOADING_REQUESTED]: 'Assets Upload Requested',
    [ComicBookPrintState.ASSETS_UPLOADING_FAILED]: 'Assets Upload Failed',
    [ComicBookPrintState.COLLECTION_CREATION_REQUESTED]: 'Collection Creation Requsted',
    [ComicBookPrintState.COLLECTION_CREATION_FAILED]: 'Collection Creation Failed',
    [ComicBookPrintState.COLLECTION_ASSIGNED]: 'Collection Assigned',
    [ComicBookPrintState.CM_CREATION_REQUESTED]: 'Candy Machine Creation Requested',
    [ComicBookPrintState.CM_CREATION_FAILED]: 'Candy Machine Creation Failed',
    [ComicBookPrintState.CM_CREATED]: 'Candy Machine Created',
    [ComicBookPrintState.CM_ITEMS_INSERT_REQUESTED]: 'Candy Machine Insert Requested',
    [ComicBookPrintState.CM_ITEMS_INSERT_FAILED]: 'Candy Machine Insert Failed',
    [ComicBookPrintState.CM_ITEMS_INSERTED]: 'Candy Machine Items Inserted',
};

export enum ComicBookPrintDistributionChannel {
    BLOCKCHAIN = 'BLOCKCHAIN',
}

export type ComicBookPrintDataApproval = {
    url: string;
    approved: boolean;
    approvedBy: string;
    approvedAt: number;
};

export enum ComicBookFormat {
    ITALIAN = 'ITALIAN',
    US = 'US',
    BD = 'BD',
}
export type ComicBookFormatKey = keyof typeof ComicBookFormatNames;

export enum MintRarity {
    COMMON = 'COMMON',
    RARE = 'RARE',
    EPIC = 'EPIC',
    ULTIMATE = 'ULTIMATE',
}

export const MintRarityRank = {
    [MintRarity.COMMON]: 0,
    [MintRarity.RARE]: 1,
    [MintRarity.EPIC]: 2,
    [MintRarity.ULTIMATE]: 3,
};

export enum MintFeature {
    HARD_COVER = 'HARD_COVER',
    FULL_ART_COVER = 'FULL_ART_COVER',
    SIGNATURE = 'SIGNATURE',
    REMARK = 'REMARK',
    SKETCH = 'SKETCH',
}

export const MintFeatureNames: { [key in MintFeature]: string } = {
    [MintFeature.HARD_COVER]: 'Hard Cover',
    [MintFeature.SIGNATURE]: 'Signature',
    [MintFeature.REMARK]: 'Remark',
    [MintFeature.FULL_ART_COVER]: 'Full Art Cover',
    [MintFeature.SKETCH]: 'Sketch',
};

export type MintFeatureKey = keyof typeof MintFeatureNames;

export const MintRarityNames: { [key in MintRarity]: string } = {
    [MintRarity.COMMON]: 'Common',
    [MintRarity.RARE]: 'Rare',
    [MintRarity.EPIC]: 'Epic',
    [MintRarity.ULTIMATE]: 'Ultimate',
};

export type MintRarityKey = keyof typeof MintRarityNames;

export const ComicBookContentRatingNames: { [key in ComicBookContentRating]: string } = {
    [ComicBookContentRating.EVERYONE]: 'Everyone (E)',
    [ComicBookContentRating.TEEN]: 'Teen (T)',
    [ComicBookContentRating.TEEN_PLUS]: 'Teen plus (T+)',
    [ComicBookContentRating.MATURE]: 'Mature (M)',
};
export type ContentRatingKey = keyof typeof ComicBookContentRatingNames;

export const ComicBookColoringNames: { [key in ComicBookColoring]: string } = {
    [ComicBookColoring.GRAYSCALE]: 'Grayscale',
    [ComicBookColoring.FULL_COLORS]: 'Full Colors',
};
export type ColoringKey = keyof typeof ComicBookColoringNames;

export const ComicBookFormatNames: { [key in ComicBookFormat]: string } = {
    [ComicBookFormat.ITALIAN]: 'Italian Format',
    [ComicBookFormat.US]: 'US Format',
    [ComicBookFormat.BD]: 'Bande Dessinée (BD)',
};

export type ComicBookPrint = {
    id: string;
    print: number;
    distributionChannel: ComicBookPrintDistributionChannel;
    quantity: number;
    comicBook: {
        title?: string;
        bookNumber?: string;
        id: string;
    };
    candyMachineDetails?: IViewCandyMachine;
    comicBookDetails?: IComicBook;
    state: ComicBookPrintState;
    nftImages: ComicBookPrintDataApproval;
    nftData: ComicBookPrintDataApproval;
    nftAssetsUrl: string;
    features: ComicBookFeature[];
    covers: ComicBookCover[];
    blockchainData: {
        collection: {
            description: string;
        };
        cm: {
            mint?: string;
            groups: PrintGroup[];
        };
    };
};

export type ComicBookSeries = {
    id: string;
    title: string;
    description: string;
    imgId: string;
    imgSquareId: string;
    blockchainData?: {
        collection: {
            symbol: string;
            description: string;
            externalUrl: string;
            royalty: string;
            mint: string;
        };
        treasuryMint: string;
        seriesAuthority?: string;
    };
};

export type Author = {
    author: {
        id: string;
        name: string;
        surname: string;
        bio: string;
        imgId: string | null;
        fullName?: string;
        tags: string[];
    };

    headliner?: boolean;
};

export type PrintAuthor = {
    id: string;
    name: string;
    surname: string;
    bio: string;
    imgId: string;
    fullName?: string;
    tags: string[];
};

export type Universe = {
    id: string;
    name: string;
    description: string;
};

export type ComicBookCover = {
    id: string;
    author: Author | PrintAuthor;
    quantity: number;
    rarity: MintRarity;
    description: string;
    imgSquareId: string;
    imgId: string;
    comicBook: {
        id: string;
    };
    parent?: {
        id: string;
    };
};

enum PrintItemType {
    COMIC_BOOK = 'COMIC_BOOK',
}

export type Attribute = {
    id: string;
    printItem: string;
    traitType: string;
    value: string;
    additional: boolean;
};

export type PrintItem = {
    id: string;
    name: string;
    issue: number;
    imageUri: string;
    jsonUri: string;
    print: string;
    mint: string;
    inserted: boolean;
    type: PrintItemType;
    attributes: Attribute[];
    originalId: string;
    symbol: string;
    description: string;
    externalUrl: string;
};

export type ComicBookFeature = {
    id: string;
    name: MintFeature;
    description: string;
    quantity: number;
    rarity: MintRarity;
    parent?: {
        id: string;
    };
    author: Author | PrintAuthor;
    layerImgId: string;
    comicBook: {
        id: string;
    };
};

export interface IComicBook {
    id: string;
    series: ComicBookSeries;
    bookNumber: number;
    universe: Universe[];
    coloring: ComicBookColoring;
    format: ComicBookFormat;
    title: string;
    description: string;
    contentRating: ComicBookContentRating;
    pages: number;
    writtenBy: Author[];
    illustratedBy: Author[];
    translatedBy: Author[];
    coloredBy: Author[];
    editedBy: Author[];
    covers: ComicBookCover[];
    features: ComicBookFeature[];
    previewId: string;
    blockchainData?: {
        symbol: string;
        description: string;
        externalUrl: string;
    };
}

type SolPayment = {
    amount: number | string;
    destination: string;
};

type TokenPayment = {
    amount: number | string;
    destination: string;
    mint: string | undefined;
    destinationAta: string;
};

export type PrintGroup = {
    label: string;
    labelName: string;
    startDate: string | undefined;
    endDate: string | undefined;
    solPayment?: SolPayment;
    tokenPayment?: TokenPayment;
    allowList?: string[];
    mintLimit?: {
        id?: number;
        limit: number | string;
    };
};

export class ComicBook implements IComicBook {
    id: string;
    series: ComicBookSeries;
    bookNumber: number;
    universe: Universe[];
    coloring: ComicBookColoring;
    format: ComicBookFormat;
    title: string;
    description: string;
    contentRating: ComicBookContentRating;
    pages: number;
    writtenBy: Author[];
    illustratedBy: Author[];
    translatedBy: Author[];
    coloredBy: Author[];
    editedBy: Author[];
    covers: ComicBookCover[];
    features: ComicBookFeature[];
    previewId: string;
    blockchainData?: {
        symbol: string;
        description: string;
        externalUrl: string;
    };

    constructor(data: IComicBook) {
        this.id = data.id;
        this.series = data.series;
        this.bookNumber = data.bookNumber;
        this.universe = data.universe;
        this.coloring = data.coloring;
        this.format = data.format;
        this.title = data.title;
        this.description = data.description;
        this.contentRating = data.contentRating;
        this.pages = data.pages;
        this.writtenBy = data.writtenBy;
        this.illustratedBy = data.illustratedBy;
        this.translatedBy = data.translatedBy;
        this.coloredBy = data.coloredBy;
        this.editedBy = data.editedBy;
        this.covers = data.covers;
        this.features = data.features;
        this.previewId = data.previewId;
        this.blockchainData = data.blockchainData;
    }
}
